import { ADMINS_SMART_REPORTS_NEW_PATH, ADMINS_SMART_REPORTS_PATH } from '@/utils/constants';
import {
  AddCircleOutline as AddCircleOutlineIcon,
  Edit as EditIcon,
  RemoveRedEye as RemoveRedEyeIcon,
} from '@mui/icons-material';
import { Avatar, Box, Button, Chip, TablePagination, Tooltip, Typography } from '@mui/material';
import { REPORT_STATUS_COLORS, REPORT_TYPE_ICONS } from './constants';
import { SmartReportListProvider, useSmartReportList } from './SmartReportListContext';

import { PER_PAGE_OPTIONS } from '@/admins/SmartReports/constants';
import { useCreateReportVersion } from '@/admins/SmartReports/hooks/useCreateReportVersion';
import { useGetReports } from '@/admins/SmartReports/hooks/useGetReports';
import { AdminsPageShell } from '@/components/AdminsPageShell';
import { useDebounce } from '@/hooks/useDebounce';
import { SmartReportStatusEnum } from '@/types/GraphqlSchema';
import { titleize } from '@/utils/helpers';
import PostAddIcon from '@mui/icons-material/PostAdd';
import { Link } from 'react-router-dom';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { CollapseStack } from '../CollapseStack';
import { SmartReportListControls } from './SmartReportListControls';
import { styles } from './styles';

const AvatarIcon = ({ reportData, size = 'medium' }) => {
  const getUserInitials = (firstName, lastName) => {
    return `${firstName[0]}${lastName[0]}`;
  };

  return (
    <Box display="flex" justifyContent="space-around" alignItems="center">
      <Typography variant="subtitle1">Created by</Typography>
      <Tooltip title={`${reportData.createdBy.firstName} ${reportData.createdBy.lastName}`}>
        <Avatar sx={styles.avatar(size)}>
          {getUserInitials(reportData.createdBy.firstName, reportData.createdBy.lastName)}
        </Avatar>
      </Tooltip>
    </Box>
  );
};

const DataChip = ({ versionData }) => (
  <Chip
    sx={styles.chip}
    label={titleize(versionData.status)}
    color={REPORT_STATUS_COLORS[versionData.status]}
  />
);

const SmartReportListComponent = () => {
  const history = useHistory();
  const { search, order, orderBy, page, perPage, setPage, setPerPage } = useSmartReportList();
  const debouncedSearch = useDebounce(search, 500);

  const { createReportVersion } = useCreateReportVersion();
  const { reports, totalCount, loading, error } = useGetReports({
    search: debouncedSearch,
    order,
    orderBy,
    perPage,
    page: page + 1,
  });

  const handleNewVersion = async (reportId) => {
    try {
      if (!reportId) return;

      const { data: createVersionResponse } = await createReportVersion(reportId);

      const newVersionId = createVersionResponse?.createSmartReportVersion?.id;
      if (newVersionId) {
        history.push(`${ADMINS_SMART_REPORTS_PATH}/${reportId}/edit`);
      }
    } catch (err) {
      console.error('Failed to create new version:', err);
    }
  };

  if (error) {
    return <Typography>Error loading reports: {error.message}</Typography>;
  }

  return (
    <AdminsPageShell
      title="Smart Reports"
      actions={
        <Button
          sx={styles.button}
          variant="contained"
          color="secondary"
          startIcon={<AddCircleOutlineIcon />}
          href={ADMINS_SMART_REPORTS_NEW_PATH}
        >
          Add New Report
        </Button>
      }
    >
      <Box sx={styles.container}>
        <SmartReportListControls />
        <CollapseStack
          loading={loading}
          data={reports}
          getDataArray={(reportData) => reportData.smartReportVersions}
          columnConfig={[
            {
              type: 'title',
              headerValue: (reportData) => `${reportData.name}`,
              headerAddition: (reportData) => <DataChip versionData={reportData} />,
              headerSubtitle: (reportData) => (
                <Typography sx={{ color: (theme) => theme.palette.text.secondary }}>
                  {`${titleize(reportData.smartReportType.name)} Report`}
                </Typography>
              ),
              value: (versionData) => `v${versionData.number}`,
              addition: (versionData) => <DataChip versionData={versionData} />,
              status: (versionData) => versionData.status,
              icon: (reportData) => REPORT_TYPE_ICONS[reportData.smartReportType.name],
              width: '1fr',
            },
            {
              type: 'date',
              headerValue: (reportData) => reportData.createdAt,
              value: (versionData) => versionData.createdAt,
              width: '140px',
            },
            {
              type: 'custom',
              headerValue: (reportData) => <AvatarIcon reportData={reportData} />,
              value: (versionData) => <AvatarIcon reportData={versionData} size="small" />,
              width: '120px',
            },
            {
              type: 'custom',
              value: (versionData, reportId) => {
                if (!versionData.isLatest) return <div />;

                const viewButton = (
                  <Link to={`${ADMINS_SMART_REPORTS_PATH}/${reportId}/view`}>
                    <RemoveRedEyeIcon color="secondary" />
                  </Link>
                );

                if (versionData.status === SmartReportStatusEnum.Draft) {
                  return (
                    <Box display="flex" gap={1}>
                      <Link to={`${ADMINS_SMART_REPORTS_PATH}/${reportId}/edit`}>
                        <EditIcon color="secondary" />
                      </Link>
                      {viewButton}
                    </Box>
                  );
                }
                if (versionData.status === SmartReportStatusEnum.Published) {
                  return (
                    <Box display="flex" gap={1}>
                      <PostAddIcon
                        color="secondary"
                        style={{ cursor: 'pointer' }}
                        onClick={() => handleNewVersion(reportId)}
                      />
                      {viewButton}
                    </Box>
                  );
                }
                return <div />;
              },
              width: '50px',
            },
          ]}
        />
        <TablePagination
          sx={styles.pagination}
          component="div"
          count={totalCount}
          labelRowsPerPage="Reports per page"
          page={page}
          color="primary"
          onPageChange={(_, page) => setPage(page)}
          rowsPerPage={perPage}
          onRowsPerPageChange={(e) => setPerPage(e.target.value)}
          rowsPerPageOptions={PER_PAGE_OPTIONS}
        />
      </Box>
    </AdminsPageShell>
  );
};

export const SmartReportList = () => {
  return (
    <SmartReportListProvider>
      <SmartReportListComponent />
    </SmartReportListProvider>
  );
};
